/**
 * [C] <delivery-estimate> — renders an estimated delivery date range from a
 * min/max number of business days, computed in the visitor's browser so the
 * date is always current (Shopify caches server-rendered pages).
 *
 * Data attributes:
 *   data-min            min business days until delivery (integer)
 *   data-max            max business days until delivery (integer)
 *   data-skip-weekends  "true" to count only Mon–Fri (default true)
 *   data-cutoff         optional hour (0–23). If the current local hour is
 *                       >= cutoff, counting starts from the next day.
 *   data-locale         BCP-47 locale for date formatting (default "ro-RO")
 *   data-style          "full" (default) or "compact"
 *
 * Fills the [data-delivery-range] child with the date range, either
 *   full     "miercuri, 15 mai – vineri, 17 mai"
 *   compact  "15–17 mai"  (or "30 aug – 2 sep" across a month boundary)
 * Compact is what the pill-sized stock strip on the product page uses.
 */
(function () {
  if (customElements.get('delivery-estimate')) return;

  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      const target = this.querySelector('[data-delivery-range]');
      if (!target) return;

      const min = Math.max(0, parseInt(this.dataset.min, 10) || 0);
      const max = Math.max(min, parseInt(this.dataset.max, 10) || min);
      const skipWeekends = this.dataset.skipWeekends !== 'false';
      const locale = this.dataset.locale || 'ro-RO';
      const cutoff =
        this.dataset.cutoff != null && this.dataset.cutoff !== ''
          ? parseInt(this.dataset.cutoff, 10)
          : null;

      const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6;

      const addBusinessDays = (from, days) => {
        const d = new Date(from);
        let added = 0;
        while (added < days) {
          d.setDate(d.getDate() + 1);
          if (!skipWeekends || !isWeekend(d)) added++;
        }
        // If we land on a weekend (e.g. days === 0), roll to next business day.
        if (skipWeekends) {
          while (isWeekend(d)) d.setDate(d.getDate() + 1);
        }
        return d;
      };

      const now = new Date();
      const base = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      if (cutoff !== null && !Number.isNaN(cutoff) && now.getHours() >= cutoff) {
        base.setDate(base.getDate() + 1);
      }

      const start = addBusinessDays(base, min);
      const end = addBusinessDays(base, max);

      const format = (options, fallback) => {
        try {
          const formatter = new Intl.DateTimeFormat(locale, options);
          return (d) => formatter.format(d);
        } catch (e) {
          return fallback;
        }
      };

      if (this.dataset.style === 'compact') {
        // "15–17 mai" when both ends share a month, "30 aug – 2 sep" when they don't.
        const day = format({ day: 'numeric' }, (d) => String(d.getDate()));
        const month = format({ month: 'short' }, (d) => String(d.getMonth() + 1));
        // ro-RO abbreviates months with a trailing dot ("aug.") — drop it, the pill is tight.
        const mon = (d) => month(d).replace(/\.$/, '');

        if (start.getTime() === end.getTime()) {
          target.textContent = day(start) + ' ' + mon(start);
        } else if (start.getMonth() === end.getMonth()) {
          target.textContent = day(start) + '–' + day(end) + ' ' + mon(end);
        } else {
          target.textContent = day(start) + ' ' + mon(start) + ' – ' + day(end) + ' ' + mon(end);
        }
        return;
      }

      const fmt = format({ weekday: 'long', day: 'numeric', month: 'long' }, (d) =>
        d.toLocaleDateString(locale)
      );

      target.textContent =
        start.getTime() === end.getTime()
          ? fmt(start)
          : fmt(start) + ' – ' + fmt(end);
    }
  }

  customElements.define('delivery-estimate', DeliveryEstimate);
})();
